import { Job, ShippingMode, Status, Stop, StopType } from '../apis/dtos';
import { ShipmentDetailValue } from '../components/shipmentDetailsLocation/ShipmentDetailsLocation';
import { IsoToDate, IsoToDateAtTime, Stringable } from './display-utilities';
import { DateLabels } from '../constants/dateLabels';
import { ModeCode } from '../constants/modeCode';

export const getPickupValues = (
  shipment: Job,
  pickup: Stop,
  translate: (key: string, replaceValues: Stringable[]) => string
): ShipmentDetailValue[] => {
  const isAirOrOcean =
    isAirShipment(shipment.modeCode) || isOceanShipment(shipment.mode);
  const { label, value } = getPickupDateInfo(pickup, isAirOrOcean);

  return [
    {
      label,
      value: isAirOrOcean
        ? IsoToDate(value)
        : IsoToDateAtTime(value, translate),
    },
  ];
};

export const getContainerDeliveryValues = (
  shipment: Job,
  deliveredContainerCount: number,
  containerCount: number
): ShipmentDetailValue[] => {
  const values: ShipmentDetailValue[] = [];
  const { estimatedNextDelivery, estimatedFullDelivery, mostRecentDelivery } =
    getContainerDates(shipment);

  // Display Est First Delivery when None Delivered out of multiple containers
  if (deliveredContainerCount === 0 && containerCount > 1) {
    values.push({
      label: DateLabels.ESTIMATED_FIRST_DELIVERY,
      value: IsoToDate(estimatedNextDelivery),
    });
  }

  // Display Est. Next Delivery when One delivered out of more than two containers
  if (deliveredContainerCount === 1 && containerCount > 2) {
    values.push({
      label: DateLabels.ESTIMATED_NEXT_DELIVERY,
      value: IsoToDate(estimatedNextDelivery),
    });
  }

  // Display this when not fully delivered
  if (deliveredContainerCount < containerCount) {
    values.push({
      label: DateLabels.ESTIMATED_DELIVERY_IN_FULL,
      value: IsoToDate(estimatedFullDelivery),
    });
  }

  // Display when One container delivered out of multiple containers
  if (deliveredContainerCount === 1 && containerCount > 1) {
    values.push({
      label: DateLabels.FIRST_DELIVERY_MADE_ON,
      value: IsoToDate(mostRecentDelivery),
    });
  }

  // Display when multiple containers delivered and delivery in full did not happen
  if (deliveredContainerCount > 1 && deliveredContainerCount < containerCount) {
    values.push({
      label: DateLabels.MOST_RECENT_DELIVERY_MADE_ON,
      value: IsoToDate(mostRecentDelivery),
    });
  }

  // Display when all containers are delivered
  if (deliveredContainerCount === containerCount) {
    values.push({
      label: DateLabels.DELIVERED_IN_FULL,
      value: IsoToDate(mostRecentDelivery),
    });
  }

  // Always display the delivered container count
  values.push({
    label: DateLabels.DELIVERED,
    value: `${deliveredContainerCount} of ${containerCount}`,
  });

  return values;
};

export const getDeliveryValues = (
  shipment: Job,
  deliverTo: Stop,
  translate: (key: string, replaceValues: Stringable[]) => string
): ShipmentDetailValue[] => {
  const values: ShipmentDetailValue[] = [];

  // When the shipment is an Air shipment
  if (isAirShipment(shipment.modeCode)) {
    if (deliverTo.departed) {
      values.push({
        label: DateLabels.ACTUAL_DATE,
        value: IsoToDate(deliverTo.departed),
      });
      return values;
    }

    if (deliverTo.requested) {
      values.push({
        label: DateLabels.REQUESTED,
        value: IsoToDate(deliverTo.requested),
      });
      return values;
    }

    return values;
  }

  // When the shipment is an Ocean shipment
  if (isOceanShipment(shipment.mode)) {
    const containerCount = shipment?.containers?.length ?? 0;
    const deliveredContainerCount =
      shipment?.containers?.filter((c) => c.status === Status.Delivered)
        .length ?? 0;
    const dropStops = shipment.stops.filter(
      (s) =>
        s.stopType === StopType.Drop ||
        s.stopType === StopType.DestinationAirport ||
        s.stopType === StopType.DestinationPort
    );

    // when the shipment has only one stop
    if (dropStops.length === 1) {
      if (deliveredContainerCount === containerCount) {
        values.push({
          label: DateLabels.ACTUAL_DATE,
          value: IsoToDate(deliverTo.arrived),
        });
        return values;
      }

      values.push({
        label: DateLabels.ESTIMATED_DELIVERY,
        value: IsoToDate(deliverTo.eta),
      });
      return values;
    }

    const containerDeliveryValues = getContainerDeliveryValues(
      shipment,
      deliveredContainerCount,
      containerCount
    );
    values.push(...containerDeliveryValues);
    return values;
  }

  // When the shipment is a Surface shipment
  const { label, value } = getDeliveryDateInfo(deliverTo);
  values.push({ label, value: IsoToDateAtTime(value, translate) });
  return values;
};

export const isAirShipment = (modeCode: string): boolean => {
  return modeCode === ModeCode.GfsAir;
};

export const isOceanShipment = (mode: ShippingMode): boolean => {
  return mode === ShippingMode.Ocean;
};

const getPickupDateInfo = (pickup: Stop, isAirOrOcean: boolean) => {
  if (isAirOrOcean) {
    if (pickup.departed) {
      return { label: DateLabels.ACTUAL_DATE, value: pickup.departed };
    }

    if (pickup.requested) {
      return { label: DateLabels.REQUESTED_DATE, value: pickup.requested };
    }
  } else {
    if (pickup.arrived) {
      return { label: DateLabels.ACTUAL_DATE, value: pickup.arrived };
    }

    if (pickup.scheduledCloseDateTime) {
      return {
        label: DateLabels.SCHEDULED_DATE,
        value: pickup.scheduledCloseDateTime,
      };
    }

    if (pickup.scheduledOpenDateTime) {
      return {
        label: DateLabels.SCHEDULED_DATE,
        value: pickup.scheduledOpenDateTime,
      };
    }

    if (pickup.requested) {
      return { label: DateLabels.REQUESTED_DATE, value: pickup.requested };
    }
  }

  return { label: DateLabels.ACTUAL_DATE, value: '' };
};

const getDeliveryDateInfo = (deliverTo: Stop) => {
  if (deliverTo.arrived) {
    return { label: DateLabels.ACTUAL_DATE, value: deliverTo.arrived };
  }

  if (deliverTo.scheduledCloseDateTime) {
    return {
      label: DateLabels.SCHEDULED_DATE,
      value: deliverTo.scheduledCloseDateTime,
    };
  }

  if (deliverTo.scheduledOpenDateTime) {
    return {
      label: DateLabels.SCHEDULED_DATE,
      value: deliverTo.scheduledOpenDateTime,
    };
  }

  if (deliverTo.requested) {
    return { label: DateLabels.REQUESTED_DATE, value: deliverTo.requested };
  }

  return { label: DateLabels.SCHEDULED_DATE, value: '' };
};

const getContainerDates = (shipment: Job) => {
  const estimatedDates = shipment.containers
    .filter((c) => c.status !== Status.Delivered && c.estimatedDelivery)
    .map((c) => new Date(c.estimatedDelivery).getTime());

  const deliveredDates = shipment.containers
    .filter((c) => c.status === Status.Delivered && c.delivered)
    .map((c) => new Date(c.delivered ?? '').getTime());

  return {
    estimatedNextDelivery:
      estimatedDates.length > 0
        ? new Date(Math.min(...estimatedDates)).toISOString()
        : null,
    estimatedFullDelivery:
      estimatedDates.length > 0
        ? new Date(Math.max(...estimatedDates)).toISOString()
        : null,
    mostRecentDelivery:
      deliveredDates.length > 0
        ? new Date(Math.max(...deliveredDates)).toISOString()
        : null,
  };
};
